(() => {
  'use strict';
  const initSliders = (scope = document) => {
    const sliders = Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-mrm-slider]:not([data-mrm-slider-bound])') : []);
    sliders.forEach(slider => {
      slider.dataset.mrmSliderBound = 'true';
      const track = slider.querySelector('[data-mrm-slider-track]');
      const slides = Array.from(slider.querySelectorAll('[data-mrm-slide]'));
      const previous = slider.querySelector('[data-mrm-slider-prev]');
      const next = slider.querySelector('[data-mrm-slider-next]');
      const status = slider.querySelector('[data-mrm-slider-status]');
      if (!track || !slides.length) return;
      let index = 0;
      let timer = null;
      const update = (newIndex) => {
        index = (newIndex + slides.length) % slides.length;
        track.style.transform = `translate3d(-${index * 100}%,0,0)`;
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
          slide.setAttribute('aria-hidden', String(slideIndex !== index));
        });
        if (status) status.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      };
      const stop = () => { if (timer) window.clearInterval(timer); timer = null; };
      const start = () => {
        stop();
        const delay = Number(slider.dataset.autoplay) || 6000;
        if (slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
          timer = window.setInterval(() => {
            if (!slider.isConnected) return stop();
            if (!document.hidden) update(index + 1);
          }, delay);
        }
      };
      previous?.addEventListener('click', () => { update(index - 1); start(); });
      next?.addEventListener('click', () => { update(index + 1); start(); });
      slider.addEventListener('pointerenter', stop);
      slider.addEventListener('pointerleave', start);
      slider.addEventListener('focusin', stop);
      slider.addEventListener('focusout', start);
      if (slides.length < 2) {
        previous?.setAttribute('disabled', 'disabled');
        next?.setAttribute('disabled', 'disabled');
      }
      update(0);
      start();
    });
  };

  const initProductHighlights = (scope = document) => {
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-product-highlight-open]:not([data-highlight-bound])') : []).forEach(button => {
      button.dataset.highlightBound = 'true';
      button.addEventListener('click', () => {
        const section = button.closest('[data-section-id]');
        const dialog = section?.querySelector('[data-product-info-dialog]');
        if (dialog?.showModal) {
          dialog.showModal();
          return;
        }
        const details = section?.querySelector('[data-product-highlights]');
        if (!details) return;
        details.open = true;
        details.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
      });
    });
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-product-info-dialog]:not([data-dialog-bound])') : []).forEach(dialog => {
      dialog.dataset.dialogBound = 'true';
      dialog.querySelector('[data-product-info-close]')?.addEventListener('click', () => dialog.close());
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close(); });
    });
  };

  const initPaymentTabs = (scope = document) => {
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-payment-tabs]:not([data-payment-bound])') : []).forEach(tabs => {
      tabs.dataset.paymentBound = 'true';
      const buttons = Array.from(tabs.querySelectorAll('[data-payment-tab]'));
      const panels = Array.from(tabs.querySelectorAll('[data-payment-panel]'));
      const activate = value => {
        buttons.forEach(button => {
          const active = button.dataset.paymentTab === value;
          button.setAttribute('aria-selected', String(active));
          button.tabIndex = active ? 0 : -1;
        });
        panels.forEach(panel => { panel.hidden = panel.dataset.paymentPanel !== value; });
      };
      buttons.forEach(button => button.addEventListener('click', () => activate(button.dataset.paymentTab)));
      activate(buttons.find(button => button.getAttribute('aria-selected') === 'true')?.dataset.paymentTab || buttons[0]?.dataset.paymentTab);
    });
  };

  const initCustomProductDialogs = (scope = document) => {
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-product-custom-dialog]:not([data-custom-dialog-bound])') : []).forEach(dialog => {
      dialog.dataset.customDialogBound = 'true';
      const section = dialog.closest('[data-product-section]');
      const open = trigger => {
        if (dialog.showModal) dialog.showModal();
        else dialog.setAttribute('open', 'open');
        window.requestAnimationFrame(() => dialog.querySelector('input:not([type="hidden"])')?.focus());
        trigger?.setAttribute('aria-expanded', 'true');
      };
      const close = () => {
        if (dialog.close) dialog.close();
        else dialog.removeAttribute('open');
        section?.querySelectorAll('[data-product-custom-open]').forEach(button => button.setAttribute('aria-expanded', 'false'));
      };
      section?.querySelectorAll('[data-product-custom-open]').forEach(button => {
        if (button.dataset.customOpenBound === 'true') return;
        button.dataset.customOpenBound = 'true';
        button.setAttribute('aria-expanded', 'false');
        button.addEventListener('click', () => open(button));
      });
      dialog.querySelector('[data-product-custom-close]')?.addEventListener('click', close);
      dialog.addEventListener('click', event => { if (event.target === dialog) close(); });
    });
  };

  const initStickyProductBars = (scope = document) => {
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-product-section]:not([data-sticky-bound])') : []).forEach(section => {
      section.dataset.stickyBound = 'true';
      const bar = section.querySelector('[data-sticky-product-bar]');
      const trigger = section.querySelector('[data-primary-buy-row]');
      if (!bar || !trigger) return;
      const select = section.querySelector('[data-product-variant-select]');
      const stickyId = bar.querySelector('[data-sticky-variant-id]');
      const stickyPrice = bar.querySelector('[data-sticky-product-price]');
      const stickySubmit = bar.querySelector('[data-sticky-product-submit]');
      const syncVariant = () => {
        const option = select?.options[select.selectedIndex];
        if (!option) return;
        if (stickyId) stickyId.value = option.value;
        if (stickyPrice && option.dataset.priceHtml) stickyPrice.innerHTML = option.dataset.priceHtml;
        if (stickySubmit) {
          const available = option.dataset.available === 'true';
          stickySubmit.disabled = !available;
          stickySubmit.setAttribute('aria-disabled', String(!available));
          stickySubmit.textContent = available ? stickySubmit.dataset.addText : stickySubmit.dataset.soldText;
        }
      };
      select?.addEventListener('change', syncVariant);
      syncVariant();
      const setVisible = visible => {
        bar.classList.toggle('is-visible', visible);
        bar.setAttribute('aria-hidden', String(!visible));
      };
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => setVisible(!entries[0].isIntersecting && window.scrollY > trigger.getBoundingClientRect().top + window.scrollY), { threshold: 0 });
        observer.observe(trigger);
      } else {
        const update = () => setVisible(trigger.getBoundingClientRect().bottom < 0);
        window.addEventListener('scroll', update, { passive: true });
        update();
      }
    });
  };

  const initMarbleLinks = (scope = document) => {
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-marble-tag-link]:not([data-marble-link-bound])') : []).forEach(link => {
      link.dataset.marbleLinkBound = 'true';
      link.addEventListener('click', event => {
        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        const target = new URL(link.href, window.location.origin);
        const current = new URL(window.location.href);
        current.searchParams.forEach((value, key) => target.searchParams.append(key, value));
        window.location.assign(target.href);
      });
    });
  };

  const initMarbleMaterialFilter = (scope = document) => {
    const root = scope.querySelector?.('[data-mrm-material-filter]');
    if (!root || root.dataset.materialFilterBound === 'true') return;
    root.dataset.materialFilterBound = 'true';
    const grid = document.querySelector('[data-mrm-material-grid]');
    const cards = grid ? Array.from(grid.querySelectorAll('[data-mrm-product-card]')) : [];
    const links = Array.from(root.querySelectorAll('[data-marble-material-link]'));
    const count = document.querySelector('[data-collection-count]');
    const input = document.querySelector('[data-mrm-material-input]');
    const empty = document.querySelector('[data-mrm-material-empty]');
    if (!grid || !cards.length || !links.length) return;
    const normalize = value => String(value || '').toLocaleLowerCase('en-US').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, ' ').trim();
    const apply = key => {
      const link = links.find(option => option.dataset.marbleMaterial === key);
      const wanted = normalize(link?.dataset.marbleMaterialLabel);
      let visible = 0;
      cards.forEach(card => {
        const show = !wanted || normalize(card.dataset.mrmMaterialText).includes(wanted);
        card.hidden = !show;
        card.setAttribute('aria-hidden', String(!show));
        if (show) visible += 1;
      });
      links.forEach(option => {
        const active = Boolean(key) && option.dataset.marbleMaterial === key;
        option.classList.toggle('is-active', active);
        option.toggleAttribute('aria-current', active);
      });
      if (count) count.textContent = String(visible);
      if (input) input.value = key || '';
      if (empty) empty.hidden = visible !== 0;
      grid.classList.toggle('is-material-filtered', Boolean(wanted));
    };
    links.forEach(link => link.addEventListener('click', event => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      const key = link.dataset.marbleMaterial || '';
      const url = new URL(window.location.href);
      if (key) url.searchParams.set('mrm_material', key); else url.searchParams.delete('mrm_material');
      window.history.replaceState({}, '', url.href);
      apply(key);
    }));
    const initial = new URL(window.location.href).searchParams.get('mrm_material') || input?.value || '';
    apply(initial);
  };

  const initProductMediaCarousels = (scope = document) => {
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-product-media-carousel]:not([data-media-carousel-bound])') : []).forEach(carousel => {
      carousel.dataset.mediaCarouselBound = 'true';
      const track = carousel.querySelector('[data-product-media-track]');
      const slides = Array.from(carousel.querySelectorAll('[data-product-media-slide]'));
      const thumbs = Array.from(carousel.querySelectorAll('[data-product-media-thumb]'));
      const previous = carousel.querySelector('[data-product-media-prev]');
      const next = carousel.querySelector('[data-product-media-next]');
      const status = carousel.querySelector('[data-product-media-status]');
      if (!track || !slides.length) return;
      let index = Math.max(0, slides.findIndex(slide => slide.classList.contains('is-active')));
      const render = value => {
        index = (value + slides.length) % slides.length;
        track.style.transform = 'translate3d(-' + (index * 100) + '%,0,0)';
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        thumbs.forEach((thumb, thumbIndex) => {
          const active = thumbIndex === index;
          thumb.classList.toggle('is-active', active);
          thumb.setAttribute('aria-current', active ? 'true' : 'false');
        });
        if (status) status.textContent = String(index + 1).padStart(2, '0') + ' / ' + String(slides.length).padStart(2, '0');
      };
      previous?.addEventListener('click', () => render(index - 1));
      next?.addEventListener('click', () => render(index + 1));
      thumbs.forEach((thumb, thumbIndex) => thumb.addEventListener('click', () => render(thumbIndex)));
      carousel.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); render(index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); render(index + 1); }
      });
      const viewport = carousel.querySelector('.mrm-product-media-viewport');
      let swipeStartX = null;
      viewport?.addEventListener('pointerdown', event => {
        if (event.pointerType === 'mouse') return;
        swipeStartX = event.clientX;
      });
      viewport?.addEventListener('pointerup', event => {
        if (swipeStartX === null) return;
        const distance = event.clientX - swipeStartX;
        swipeStartX = null;
        if (Math.abs(distance) < 45) return;
        render(index + (distance < 0 ? 1 : -1));
      });
      viewport?.addEventListener('pointercancel', () => { swipeStartX = null; });
      const variantSelect = carousel.closest('[data-product-section]')?.querySelector('[data-product-variant-select]');
      variantSelect?.addEventListener('change', () => {
        const mediaId = variantSelect.options[variantSelect.selectedIndex]?.dataset.featuredMediaId;
        const mediaIndex = slides.findIndex(slide => slide.dataset.mediaId === mediaId);
        if (mediaIndex >= 0) render(mediaIndex);
      });
      if (slides.length < 2) {
        previous?.setAttribute('disabled', 'disabled');
        next?.setAttribute('disabled', 'disabled');
      }
      render(index);
    });
  };

  const initProductGalleryDialogs = (scope = document) => {
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-product-gallery]:not([data-product-gallery-bound])') : []).forEach(gallery => {
      gallery.dataset.productGalleryBound = 'true';
      const section = gallery.closest('[data-product-section]');
      const dialog = section?.querySelector('[data-product-gallery-dialog]');
      const openers = Array.from(gallery.querySelectorAll('[data-product-gallery-open]'));
      const slides = Array.from(dialog?.querySelectorAll('[data-product-gallery-slide]') || []);
      const previous = dialog?.querySelector('[data-product-gallery-prev]');
      const next = dialog?.querySelector('[data-product-gallery-next]');
      const close = dialog?.querySelector('[data-product-gallery-close]');
      const count = dialog?.querySelector('[data-product-gallery-count]');
      if (!dialog || !slides.length) return;
      let index = 0;
      const render = value => {
        index = (value + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === index;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', String(!active));
        });
        if (count) count.textContent = String(index + 1).padStart(2, '0') + ' / ' + String(slides.length).padStart(2, '0');
      };
      openers.forEach(opener => opener.addEventListener('click', () => {
        render(Number(opener.dataset.galleryIndex) || 0);
        if (dialog.showModal) dialog.showModal(); else dialog.setAttribute('open', 'open');
      }));
      previous?.addEventListener('click', () => render(index - 1));
      next?.addEventListener('click', () => render(index + 1));
      close?.addEventListener('click', () => dialog.close?.());
      dialog.addEventListener('click', event => { if (event.target === dialog) dialog.close?.(); });
      dialog.addEventListener('keydown', event => {
        if (event.key === 'ArrowLeft') { event.preventDefault(); render(index - 1); }
        if (event.key === 'ArrowRight') { event.preventDefault(); render(index + 1); }
      });
      render(0);
    });
  };

  const initPopup = (scope = document) => {
    const popup = scope.querySelector ? scope.querySelector('[data-site-popup]:not([data-popup-bound])') : null;
    if (!popup) return;
    popup.dataset.popupBound = 'true';
    const storageKey = 'mrm-popup-dismissed-' + (popup.dataset.popupCampaign || 'campaign-1');
    const close = () => {
      popup.classList.remove('is-visible');
      popup.setAttribute('aria-hidden', 'true');
      if (popup.querySelector('[data-popup-dismiss]')?.checked) {
        try { window.localStorage.setItem(storageKey, '1'); } catch (error) { /* storage can be unavailable */ }
      }
    };
    popup.querySelector('[data-popup-close]')?.addEventListener('click', close);
    popup.addEventListener('click', event => { if (event.target === popup) close(); });
    popup.querySelector('[data-popup-dismiss]')?.addEventListener('change', event => {
      if (event.target.checked) {
        try { window.localStorage.setItem(storageKey, '1'); } catch (error) { /* storage can be unavailable */ }
      }
    });
    document.addEventListener('keydown', event => { if (event.key === 'Escape' && popup.classList.contains('is-visible')) close(); });
    let dismissed = false;
    try { dismissed = window.localStorage.getItem(storageKey) === '1'; } catch (error) { /* storage can be unavailable */ }
    if (!dismissed) {
      const delay = Math.max(0, Number(popup.dataset.popupDelay) || 0) * 1000;
      window.setTimeout(() => { popup.classList.add('is-visible'); popup.setAttribute('aria-hidden', 'false'); }, delay);
    }
  };

  const initReviewSliders = (scope = document) => {
    Array.from(scope.querySelectorAll ? scope.querySelectorAll('[data-review-slider]:not([data-review-bound])') : []).forEach(slider => {
      slider.dataset.reviewBound = 'true';
      const track = slider.querySelector('[data-review-track]');
      const slides = Array.from(slider.querySelectorAll('[data-review-slide]'));
      const previous = slider.querySelector('[data-review-prev]');
      const next = slider.querySelector('[data-review-next]');
      const status = slider.querySelector('[data-review-status]');
      if (!track || !slides.length) return;
      let index = 0;
      let timer;
      const render = value => {
        index = (value + slides.length) % slides.length;
        track.style.transform = `translate3d(-${index * 100}%,0,0)`;
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
          slide.setAttribute('aria-hidden', String(slideIndex !== index));
        });
        if (status) status.textContent = `${String(index + 1).padStart(2, '0')} / ${String(slides.length).padStart(2, '0')}`;
      };
      const stop = () => window.clearInterval(timer);
      const start = () => {
        stop();
        if (slides.length > 1 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) timer = window.setInterval(() => render(index + 1), 7500);
      };
      previous?.addEventListener('click', () => { render(index - 1); start(); });
      next?.addEventListener('click', () => { render(index + 1); start(); });
      slider.addEventListener('pointerenter', stop);
      slider.addEventListener('pointerleave', start);
      if (slides.length < 2) { previous?.setAttribute('disabled', 'disabled'); next?.setAttribute('disabled', 'disabled'); }
      render(0);
      start();
    });
  };

  const initCustomerPages = (scope = document) => {
    const root = scope.querySelector ? scope : document;
    Array.from(root.querySelectorAll('[data-auth-show]:not([data-auth-bound])')).forEach(link => {
      link.dataset.authBound = 'true';
      link.addEventListener('click', event => {
        event.preventDefault();
        const target = link.dataset.authShow;
        document.querySelectorAll('[data-auth-panel]').forEach(panel => { panel.hidden = panel.dataset.authPanel !== target; });
        history.replaceState(null, '', target === 'recover' ? '#recover' : '#login');
        document.querySelector(`[data-auth-panel="${target}"] input`)?.focus();
      });
    });
    if ((location.hash === '#recover' || root.querySelector('[data-recover-success]')) && document.querySelector('[data-auth-panel="recover"]')) {
      document.querySelectorAll('[data-auth-panel]').forEach(panel => { panel.hidden = panel.dataset.authPanel !== 'recover'; });
    }
    const registerForm = root.querySelector('#MarwodeRegisterForm:not([data-register-bound])');
    if (registerForm) {
      registerForm.dataset.registerBound = 'true';
      registerForm.addEventListener('submit', () => {
        const values = Array.from(registerForm.querySelectorAll('[data-registration-detail]')).map(field => {
          const label = field.dataset.registrationLabel || field.name || 'Detail';
          return field.value ? `${label}: ${field.value}` : '';
        }).filter(Boolean);
        const note = registerForm.querySelector('[data-registration-note]');
        const phone = registerForm.querySelector('[data-registration-phone]');
        const visiblePhone = registerForm.querySelector('#RegisterPhone');
        if (note) note.value = values.length ? `Registration details\n${values.join('\n')}` : '';
        if (phone && visiblePhone) phone.value = visiblePhone.value;
      });
    }
    Array.from(root.querySelectorAll('[data-password-toggle]:not([data-password-bound])')).forEach(button => {
      button.dataset.passwordBound = 'true';
      button.addEventListener('click', () => {
        const input = button.closest('.mrm-password-wrap')?.querySelector('input');
        if (!input) return;
        const show = input.type === 'password';
        input.type = show ? 'text' : 'password';
        button.textContent = show ? 'Hide' : 'Show';
        button.setAttribute('aria-label', show ? 'Hide password' : 'Show password');
      });
    });
    Array.from(root.querySelectorAll('[data-address-delete-form]:not([data-delete-bound])')).forEach(form => {
      form.dataset.deleteBound = 'true';
      form.addEventListener('submit', event => {
        const message = form.querySelector('[data-confirm]')?.dataset.confirm || 'Are you sure?';
        if (!window.confirm(message)) event.preventDefault();
      });
    });
    Array.from(root.querySelectorAll('[data-address-form]:not([data-country-bound])')).forEach(form => {
      form.dataset.countryBound = 'true';
      const country = form.querySelector('[data-address-country]');
      const province = form.querySelector('[data-address-province]');
      if (!country || !province) return;
      const container = province.parentElement;
      if (window.Shopify?.CountryProvinceSelector) {
        new window.Shopify.CountryProvinceSelector(country.id, province.id, { hideElement: container?.id });
      } else if (province.options.length === 0) {
        container?.setAttribute('hidden', 'hidden');
      }
    });
  };

  const initMarwodeOriginal = (scope = document) => {
    const header = document.querySelector('[data-mrm-header]');
    if (header && header.dataset.originalBound !== 'true') {
      header.dataset.originalBound = 'true';
      const syncHeader = () => document.body.classList.toggle('mrm-scrolled', window.scrollY > 60);
      syncHeader();
      window.addEventListener('scroll', syncHeader, { passive: true });
    }
    initSliders(scope);
    initReviewSliders(scope);
    initProductHighlights(scope);
    initPaymentTabs(scope);
    initCustomProductDialogs(scope);
    initStickyProductBars(scope);
    initMarbleLinks(scope);
      initMarbleMaterialFilter(scope);
      initProductMediaCarousels(scope);
      initProductGalleryDialogs(scope);
      initCustomerPages(scope);
    initPopup(scope);
    const targets = Array.from(scope.querySelectorAll ? scope.querySelectorAll('.mrm-reveal:not([data-reveal-bound])') : []);
    if (!targets.length) return;
    if (!('IntersectionObserver' in window) || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      targets.forEach(el => { el.dataset.revealBound='true'; el.classList.add('mrm-in-view'); }); return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => { if (entry.isIntersecting) { entry.target.classList.add('mrm-in-view'); observer.unobserve(entry.target); } });
    }, { threshold:.1, rootMargin:'0px 0px -50px 0px' });
    targets.forEach(el => { el.dataset.revealBound='true'; observer.observe(el); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initMarwodeOriginal(document), { once:true });
  else initMarwodeOriginal(document);
  document.addEventListener('shopify:section:load', e => initMarwodeOriginal(e.target));
})();
